import { Box, Flex } from '@chakra-ui/react';
import { KTag, KText } from '@/ui-library/components';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { PlusCircle } from '@kula-ai/icons/dual-tone/PlusCircle';
import { KSwitch } from '@/ui-library/components/molecules/KSwitch';

interface SectionHeadingProps {
  title: string;
  count?: number;
  showAddButton?: boolean;
  onAddClick?: (e: any) => void;
  textProps?: any;
  background?: string;
  addButtonText?: string;
  tooltipText?: string;
  headingProps?: any;
  showToggle?: boolean;
  toggleValue?: boolean;
  onToggleChange?: (value: boolean) => void;
  toggleDisabled?: boolean;
  toggleTooltipLabel?: string;
}

interface SectionWithHeadingProps {
  title: string;
  count?: number;
  showAddButton?: boolean;
  onAddClick?: () => void;
  addButtonText?: string;
  showToggle?: boolean;
  toggleValue?: boolean;
  onToggleChange?: (value: boolean) => void;
  toggleDisabled?: boolean;
  toggleTooltipLabel?: string;
  children: React.ReactNode;
  background?: string;
  containerStyles?: any;
  contentStyles?: any;
}

export const SectionHeading = ({
  title,
  count = 0,
  showAddButton = false,
  onAddClick = () => {},
  textProps,
  background = 'secondary-50',
  addButtonText = 'Add field',
  tooltipText,
  headingProps,
  showToggle = false,
  toggleValue = false,
  onToggleChange = () => {},
  toggleDisabled = false,
  toggleTooltipLabel,
  ...props
}: SectionHeadingProps) => (
  <>
    <Flex alignItems="center" mb="12px" gap="12px" w="100%" {...headingProps}>
      <KTag
        width="fit-content"
        borderRadius="6px"
        as="div"
        bgColor={background}
        {...props}
        value={
          <Flex alignItems="center" gap="6px" w="max-content">
            <KText
              size="sm-medium"
              color="secondary-900"
              as="span"
              {...textProps}
            >
              {title}
            </KText>
            {count > 0 && (
              <Box
                bg="white"
                borderRadius="4px"
                borderWidth="1px"
                borderColor="border-color"
                px="8px"
                py="2px"
              >
                <KText as="span">{count.toString()}</KText>
              </Box>
            )}
            {showToggle &&
              (toggleDisabled && toggleTooltipLabel ? (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Box as="span" display="inline-flex">
                        <KSwitch
                          className={`ats-switch-toogle ${toggleValue ? 'active' : ''}`}
                          isChecked={toggleValue}
                          onChange={onToggleChange}
                          width="28px"
                          isDisabled
                        />
                      </Box>
                    </TooltipTrigger>
                    <TooltipContent side="top" collisionPadding={12}>
                      {toggleTooltipLabel}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <KSwitch
                  className={`ats-switch-toogle ${toggleValue ? 'active' : ''}`}
                  isChecked={toggleValue}
                  onChange={onToggleChange}
                  width="28px"
                  isDisabled={toggleDisabled}
                />
              ))}
          </Flex>
        }
      />
      <Box
        borderBottom="1px solid"
        borderColor="border-color"
        width="100%"
        height="2px"
      />
      {showAddButton && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                // Never submit an enclosing form — a bare <button> defaults to
                // type="submit", so an "Add" inside a form would submit it.
                type="button"
                variant="tertiary"
                size="sm"
                p="9px 12px !important"
                leftElement={
                  <PlusCircle
                    className="!text-primary-600"
                    height="16px"
                    width="16px"
                  />
                }
                onClick={onAddClick}
              >
                <KText size="sm-medium" color="primary-600" as="span">
                  {addButtonText}
                </KText>
              </Button>
            </TooltipTrigger>
            {tooltipText && (
              <TooltipContent side="top" collisionPadding={12}>
                {tooltipText}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
    </Flex>
  </>
);

export const SectionWithHeading: React.FC<SectionWithHeadingProps> = ({
  title,
  count,
  showAddButton = false,
  onAddClick,
  addButtonText = 'Add field',
  showToggle = false,
  toggleValue = false,
  onToggleChange,
  toggleDisabled = false,
  toggleTooltipLabel,
  children,
  background = 'secondary-50',
  containerStyles = {},
  contentStyles = {},
}) => {
  return (
    <Box {...containerStyles}>
      <Box>
        <SectionHeading
          title={title}
          count={count}
          background={background}
          showAddButton={showAddButton}
          onAddClick={onAddClick}
          addButtonText={addButtonText}
          showToggle={showToggle}
          toggleValue={toggleValue}
          onToggleChange={onToggleChange}
          toggleDisabled={toggleDisabled}
          toggleTooltipLabel={toggleTooltipLabel}
        />
      </Box>
      <Box {...contentStyles}>{children}</Box>
    </Box>
  );
};
